import moment from 'moment'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiColorPicker, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiFormRow, EuiRadioGroup, EuiSpacer, EuiSwitch, EuiText, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit } from 'api/interfaces'
import { AIEditFlyoutState } from 'api/interfaces/AIEditTypes'
import { MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { useGetAIPlanStubQuery, useAddAIPlanStubCampaignBuilderMutation } from 'api/rtkQueryApi/platform/aiPlansApi'
import { AcEuiFormRow } from 'components/Forms/AcEuiFormRow'
import { AIEditPromptContent, CampaignConstraints } from 'features/planner/components/AIEditPromptFlyout'
import { offAdCritterCustomColorOptions, offAdCritterPlatformColors, getAvailableColors, getNextAvailableColor } from 'features/planner/components/GanttChart/utils'
import { getCampaignTypeNameLong } from 'utils/CampaignType'

import { CampaignForm, AddCampaignSectionStep, CampaignFormFields } from '../../../../components/CampaignForm'

type AddCampaignStep = 'typeSelection' | 'aiPrompt' | 'offAdCritterDetails'
type SelectionCategory = 'adCritterType' | 'offAdCritter'

const standardPlatforms = ['Facebook', 'Google', 'Instagram', 'LinkedIn', 'TikTok']
const offAdCritterOptions = [...standardPlatforms.map(name => ({ id: name, label: name })), { id: '__custom__', label: 'Custom' }]

interface AddAIPlanCampaignFlyoutProps {
  aiPlanId: string
  onClose: () => void
  onSubmitAIEdit: (prompt: string, state: AIEditFlyoutState, deepThink?: boolean) => void
  isAIEditSubmitting: boolean
}

interface AIPlanPageParams {
  aiPlanId: string
}

const AddAIPlanCampaignFlyout: FC<AddAIPlanCampaignFlyoutProps> = ({ aiPlanId, onClose, onSubmitAIEdit, isAIEditSubmitting }) => {
  const { aiPlanId: routeAIPlanId } = useParams<AIPlanPageParams>()
  const { data: stub } = useGetAIPlanStubQuery({ aiPlanId: routeAIPlanId }, { skip: !routeAIPlanId })

  const [step, setStep] = useState<AddCampaignStep>('typeSelection')

  const [selectedCategory, setSelectedCategory] = useState<SelectionCategory>('adCritterType')
  const [selectedAdCritterType, setSelectedAdCritterType] = useState<MasterCampaignPlanCampaignBuilderType>(MasterCampaignPlanCampaignBuilderType.TV)
  const [selectedOffAdCritterPlatform, setSelectedOffAdCritterPlatform] = useState('')
  const [showCustomInputs, setShowCustomInputs] = useState(false)
  const [customPlatformName, setCustomPlatformName] = useState('')
  const [customPlatformColor, setCustomPlatformColor] = useState(() => getNextAvailableColor(getAvailableColors(offAdCritterCustomColorOptions, [])))

  const aiPromptSubmitRef = useRef<(() => void) | null>(null)
  const [isAIPromptSubmitDisabled, setIsAIPromptSubmitDisabled] = useState(true)
  const [deepThink, setDeepThink] = useState(false)
  const handleAIPromptDisabledChange = useCallback((disabled: boolean) => setIsAIPromptSubmitDisabled(disabled), [])

  const offAdCritterName = showCustomInputs ? customPlatformName.trim() : selectedOffAdCritterPlatform
  const offAdCritterColor = showCustomInputs ? customPlatformColor : (offAdCritterPlatformColors[selectedOffAdCritterPlatform] || '#999999')

  const [campaignBuilder, setCampaignBuilder] = useState<CampaignFormFields>({
    name: 'New Campaign',
    shortSummary: '',
    longSummary: '',
    audienceSummary: '',
    geoSummary: '',
    seasonalitySummary: '',
    campaignType: MasterCampaignPlanCampaignBuilderType.OffAdCritter,
    isOffAdCritter: true,
    offAdCritterName: '',
    offAdCritterColor: '#999999',
    retargetSite: false,
    startDate: moment().add(3, 'days'),
    intervalUnit: IntervalUnit.Month,
    intervalFrequency: 1,
    intervalDuration: 1,
    segmentTargets: [],
    zips: []
  })

  const didFireAdd = useRef(false)
  const [addCampaignBuilder, { isSuccess: isAddSuccess, isLoading: isAdding }] = useAddAIPlanStubCampaignBuilderMutation()

  useEffect(() => {
    if (didFireAdd.current && isAddSuccess) {
      didFireAdd.current = false
      onClose()
    }
  }, [didFireAdd, isAddSuccess, onClose])

  const clearOffAdCritterState = () => {
    setSelectedOffAdCritterPlatform('')
    setShowCustomInputs(false)
    setCustomPlatformName('')
  }

  const handleAdCritterTypeChange = (type: MasterCampaignPlanCampaignBuilderType) => {
    setSelectedCategory('adCritterType')
    setSelectedAdCritterType(type)
    clearOffAdCritterState()
  }

  const handleOffAdCritterSelection = (id: string) => {
    setSelectedCategory('offAdCritter')
    setSelectedOffAdCritterPlatform(id)
    if (id === '__custom__') {
      setShowCustomInputs(true)
    } else {
      setShowCustomInputs(false)
      setCustomPlatformName('')
    }
  }

  const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color) || color.startsWith('linear-gradient') || color.startsWith('radial-gradient')
  }

  const isTypeSelectionNextDisabled = () => {
    if (selectedCategory === 'offAdCritter') {
      if (!selectedOffAdCritterPlatform) return true
      if (showCustomInputs && (!customPlatformName.trim() || !isValidHexColor(customPlatformColor))) return true
    }
    return false
  }

  const isOffAdCritterAI = selectedCategory === 'offAdCritter' && !showCustomInputs

  const handleTypeSelectionNext = () => {
    if (selectedCategory === 'adCritterType' || isOffAdCritterAI) {
      setStep('aiPrompt')
    } else if (selectedCategory === 'offAdCritter') {
      setCampaignBuilder(prev => ({
        ...prev,
        offAdCritterName,
        offAdCritterColor
      }))
      setStep('offAdCritterDetails')
    }
  }

  const handleAIPromptSubmit = (prompt: string, constraints?: CampaignConstraints, submitDeepThink?: boolean) => {
    const state: AIEditFlyoutState = {
      targetType: 'AddCampaign',
      targetId: aiPlanId,
      targetLabel: isOffAdCritterAI ? `Add ${selectedOffAdCritterPlatform} Campaign` : `Add ${selectedAdCritterType} Campaign`,
      showConstraints: true,
      constraintCampaignType: isOffAdCritterAI ? undefined : (constraints?.campaignType || selectedAdCritterType),
      constraintStartDate: constraints?.startDate,
      constraintDuration: constraints?.duration,
      constraintBudget: constraints?.budget,
      campaignType: isOffAdCritterAI ? undefined : selectedAdCritterType,
      offAdCritterPlatformName: isOffAdCritterAI ? selectedOffAdCritterPlatform : undefined
    }
    onSubmitAIEdit(prompt, state, submitDeepThink)
  }

  const handleOffAdCritterSave = () => {
    if (!stub || !campaignBuilder.intervalBudget) return

    addCampaignBuilder({
      aiPlanStubId: stub.id,
      request: {
        name: campaignBuilder.name,
        shortSummary: campaignBuilder.shortSummary,
        longSummary: campaignBuilder.longSummary,
        audienceSummary: campaignBuilder.audienceSummary,
        audienceDetails: '',
        geoSummary: campaignBuilder.geoSummary,
        geoDetails: '',
        seasonalitySummary: campaignBuilder.seasonalitySummary,
        seasonalityDetails: '',
        campaignType: campaignBuilder.campaignType as unknown as string,
        isOffAdCritter: true,
        offAdCritterName: campaignBuilder.offAdCritterName,
        offAdCritterColor: campaignBuilder.offAdCritterColor,
        intervalBudget: campaignBuilder.intervalBudget!,
        retargetSite: campaignBuilder.retargetSite,
        isTotalBudget: false,
        startDate: moment(campaignBuilder.startDate).toISOString(),
        intervalUnit: campaignBuilder.intervalUnit as unknown as string,
        intervalFrequency: campaignBuilder.intervalFrequency,
        intervalDuration: campaignBuilder.intervalDuration,
        segmentTargets: campaignBuilder.segmentTargets,
        audienceSegmentLooksUp: [],
        regions: [],
        dmas: [],
        cities: [],
        zips: campaignBuilder.zips,
        cpm: campaignBuilder.cpm,
        marginOfError: campaignBuilder.marginOfError ? campaignBuilder.marginOfError / 100 : undefined,
        includeInProjections: campaignBuilder.includeInProjections || false,
        includeInGeoHeatmap: campaignBuilder.includeInGeoHeatmap || false,
        keywords: campaignBuilder.keywords,
        keywordDescription: campaignBuilder.keywordDescription,
        cpc: campaignBuilder.cpc
      }
    })
    didFireAdd.current = true
  }

  const isOffAdCritterSaveDisabled = () => {
    if (!campaignBuilder) return true
    if (!campaignBuilder.offAdCritterName) return true
    if (!campaignBuilder.offAdCritterColor || !isValidHexColor(campaignBuilder.offAdCritterColor)) return true
    if (!campaignBuilder.intervalBudget) return true
    return !campaignBuilder.name || moment(campaignBuilder.startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3 || campaignBuilder.intervalDuration <= 0
  }

  const getFlyoutTitle = () => {
    switch (step) {
      case 'typeSelection':
        return 'Add Campaign'
      case 'aiPrompt':
        return isOffAdCritterAI ? `Add ${selectedOffAdCritterPlatform} Campaign` : `Add ${selectedAdCritterType === MasterCampaignPlanCampaignBuilderType.Audio ? 'Streaming Audio' : selectedAdCritterType} Campaign`
      case 'offAdCritterDetails':
        return 'Add Campaign'
    }
  }

  const renderTypeSelection = () => (
    <>
      <EuiFormRow label='AdCritter-Integrated Channels *'>
        <>
          <EuiRadioGroup
            options={[
              { id: MasterCampaignPlanCampaignBuilderType.TV, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.TV) },
              { id: MasterCampaignPlanCampaignBuilderType.Billboard, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Billboard) },
              // HIDE_STREAMING_AUDIO_FROM_AI_PLAN - uncomment to re-enable Audio in the Add Campaign flyout
              // { id: MasterCampaignPlanCampaignBuilderType.Audio, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Audio) },
              { id: MasterCampaignPlanCampaignBuilderType.Internet, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Internet) }
            ]}
            idSelected={selectedCategory === 'adCritterType' ? selectedAdCritterType : ''}
            onChange={id => handleAdCritterTypeChange(id as MasterCampaignPlanCampaignBuilderType)}
            name='campaign type group'
          />
        </>
      </EuiFormRow>

      <EuiSpacer size='m' />

      <EuiFormRow label='Off-AdCritter Channels'>
        <>
          <EuiRadioGroup options={offAdCritterOptions} idSelected={selectedCategory === 'offAdCritter' ? (showCustomInputs ? '__custom__' : selectedOffAdCritterPlatform) : ''} onChange={handleOffAdCritterSelection} name='off AdCritter group' />
        </>
      </EuiFormRow>

      {showCustomInputs && (
        <div style={{ paddingLeft: '60px' }}>
          <EuiSpacer size='s' />
          <AcEuiFormRow label='Custom Channel Name *' isInvalid={!customPlatformName.trim()}>
            <EuiFieldText value={customPlatformName} onChange={e => setCustomPlatformName(e.target.value)} />
          </AcEuiFormRow>
          <EuiFormRow label='Color *' isInvalid={!isValidHexColor(customPlatformColor)} error={['Valid hex color is required (e.g., #999999)']}>
            <EuiColorPicker color={customPlatformColor} onChange={setCustomPlatformColor} swatches={getAvailableColors(offAdCritterCustomColorOptions, [])} />
          </EuiFormRow>
        </div>
      )}
    </>
  )

  const renderFooter = () => {
    switch (step) {
      case 'typeSelection':
        return (
          <EuiFlexGroup direction='row' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={onClose}>Cancel</EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton fill onClick={handleTypeSelectionNext} isDisabled={isTypeSelectionNextDisabled()}>
                Next
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size='xs'>
                <p>* Required fields</p>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        )
      case 'aiPrompt':
        return (
          <EuiFlexGroup direction='row' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={() => setStep('typeSelection')} disabled={isAIEditSubmitting}>
                Back
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={() => aiPromptSubmitRef.current?.()} fill disabled={isAIPromptSubmitDisabled} isLoading={isAIEditSubmitting}>
                Submit
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiSwitch label='Deep Think' checked={deepThink} onChange={e => setDeepThink(e.target.checked)} disabled={isAIEditSubmitting} compressed />
            </EuiFlexItem>
            {isAIEditSubmitting && (
              <EuiFlexItem grow={false}>
                <EuiText size='xs' style={{ backgroundColor: '#D3DAE6', borderRadius: 4, padding: '4px 8px' }}>
                  Complex edit. Allow 30 seconds to 2 minutes.
                </EuiText>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        )
      case 'offAdCritterDetails':
        return (
          <EuiFlexGroup direction='row' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={() => setStep('typeSelection')}>Back</EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton fill onClick={handleOffAdCritterSave} isLoading={isAdding} isDisabled={isOffAdCritterSaveDisabled()}>
                Save
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size='xs'>
                <p>* Required fields</p>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        )
    }
  }

  return (
    <AcFlyout onClose={onClose} size={step === 'aiPrompt' ? 'm' : undefined}>
      <EuiFlyoutHeader hasBorder={step === 'aiPrompt'}>
        <EuiTitle size='m'>
          <h2>{getFlyoutTitle()}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        {step === 'typeSelection' && renderTypeSelection()}

        {step === 'aiPrompt' && (
          <AIEditPromptContent
            targetType='AddCampaign'
            targetLabel={isOffAdCritterAI ? `Add ${selectedOffAdCritterPlatform} Campaign` : `Add ${selectedAdCritterType} Campaign`}
            onSubmit={handleAIPromptSubmit}
            isSubmitting={isAIEditSubmitting}
            showConstraints
            hideChannelType
            deepThink={deepThink}
            campaignType={isOffAdCritterAI ? undefined : selectedAdCritterType}
            onSubmitRef={aiPromptSubmitRef}
            onSubmitDisabledChange={handleAIPromptDisabledChange}
          />
        )}

        {step === 'offAdCritterDetails' && (
          <CampaignForm
            campaignBuilder={campaignBuilder}
            setCampaignBuilder={setCampaignBuilder}
            step={AddCampaignSectionStep.CampaignDetails}
            offAdCritterCustomOptions={[]}
          />
        )}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        {renderFooter()}
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default AddAIPlanCampaignFlyout
